import React, { Fragment, useState, useEffect, useCallback } from 'react';
import { GetList, AddItem, UpdateItem, DeleteItem } from '../../REST/crud';
import { GetPropertyAttrs } from '../../REST/properties'
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import { useForm, Controller } from "react-hook-form";
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';
import PageShell from '../../themes/components/PageShell';
import PageHeader from '../../themes/components/PageHeader';
import PageContent from '../../themes/components/PageContent';
import Action from '../../themes/components/Action';
import ResponsiveDataGrid from '../../themes/components/ResponsiveDataGrid';
import WorkflowDialogShell from '../../themes/components/WorkflowDialogShell';

export default function PropertyTypes() {
    const [mode, setMode] = useState(null)
    const [attrs, setAttrs] = useState([])
    const navigate = useNavigate()
    const [rowData, setRowData] = useState(null)

    useEffect(() => {
        (async () => {
            const attributes = await GetPropertyAttrs()
            setAttrs(attributes.data)
        })()
    }, [])

    const columns = [
        { field: 'name', headerName: 'Name', flex: 0.4 },
        { 
            field: 'attributes', 
            headerName: 'Attributes', 
            flex: 0.3,
            valueGetter: (params) => {
                const attrIds = params.row.attributes || []
                const attrNames = attrIds.map(id => {
                    const attr = attrs.find(a => a._id === id)
                    return attr?.name || id
                })
                return attrNames.join(', ')
            }
        },
        { 
            field: 'hasWaitlist', 
            headerName: 'Has Waitlist', 
            flex: 0.1,
            type: 'boolean',
            valueGetter: (params) => params.row.hasWaitlist || false
        },
        {
            field: 'actions', 
            headerName: 'Actions', 
            flex: 0.2, 
            renderCell: (params) => {
                return (
                    <Fragment>
                        <IconButton onClick={(e) => { e.stopPropagation(); handleEdit(params.row) }}><EditIcon /></IconButton>
                        <IconButton onClick={(e) => { e.stopPropagation(); handleDelete(params.row._id) }}><DeleteIcon /></IconButton>
                    </Fragment>
                )
            }
        }
    ];

    const fetchPropertyTypes = useCallback(async (page, limit, filters, sort) => {
        const params = {
            page,
            limit
        }
        
        if (Object.keys(filters).length > 0) {
            params.filters = JSON.stringify(filters)
        }
        
        if (sort) {
            params.sort = JSON.stringify(sort)
        }
        
        const result = await GetList('PropertyType', params);

        if (result.data?.data) {
            return {
                data: result.data.data,
                pagination: result.data.pagination
            };
        }

        if (Array.isArray(result.data)) {
            return {
                data: result.data,
                pagination: { total: result.data.length, page: 1, totalPages: 1 }
            };
        }

        return { data: [], pagination: { total: 0, page: 1, totalPages: 0 } };
    }, []);

    const handleMode = (mode) => { setMode(mode) }

    const handleData = (data) => {
        const options = document.getElementById('attrsSelect').selectedOptions
        const values = Array.from(options).map(({ value }) => value);
        
        const payload = { 
            ...data, 
            attributes: values,
            hasWaitlist: data.hasWaitlist || false
        };
        
        if (rowData && rowData._id) {
            UpdateItem('PropertyType', rowData._id, payload).then(() => {
                setMode(null)
                setRowData(null)
            })
        } else {
            AddItem('PropertyType', payload).then(() => {
                setMode(null)
                setRowData(null)
            })
        }
    }

    const handleEdit = (value) => {
        setRowData(value)
        handleMode(true)
    }

    const handleAdd = () => {
        setRowData(null)
        handleMode(true)
    }

    const handleDelete = (id) => {
        DeleteItem('PropertyType', id).then(() => {
            // Grid will auto-refresh
        })
    }

    return <Fragment>
        <PageShell>
            <PageHeader
                title="Property Types"
                back
                onBack={() => navigate(-1)}
                actions={
                    <Action
                        primary
                        icon={<AddIcon />}
                        label="Add Property Type"
                        onClick={handleAdd}
                    />
                }
            />

            {mode !== null && <FormDialog open={mode !== null} handleClose={() => { handleMode(null); setRowData(null) }} handleSave={handleData} attrs={attrs} prpData={rowData} />}

            <PageContent fill>
                <ResponsiveDataGrid
                    columns={columns}
                    fetchData={fetchPropertyTypes}
                    initialPageSize={10}
                    pageSizeOptions={[10, 25, 50, 100]}
                    getRowId={(i) => i?._id}
                />
            </PageContent>
        </PageShell>
    </Fragment>
}


function FormDialog({ open = false, data, handleClose, handleSave, attrs = [], prpData }) {
    const schema = yup.object({
        name: yup.string().trim().required(),
        hasWaitlist: yup.boolean()
    }).required();

    const { control, handleSubmit, formState: { errors }, clearErrors, reset } = useForm({
        defaultValues: {
            name: '',
            hasWaitlist: false
        },
        resolver: yupResolver(schema)
    });

    useEffect(() => {
        if (prpData) {
            const { name, attributes, hasWaitlist } = prpData
            reset({ name: name, attributes: attributes, hasWaitlist: hasWaitlist || false });
        }
    }, [prpData, reset]);

    useEffect(() => {
        clearErrors()
        reset()
    }, [open, clearErrors, reset])

    const onSubmit = handleSave

    const isError = (val) => val !== undefined && val !== null && typeof val === 'object'


    return <WorkflowDialogShell
        open={open}
        onClose={handleClose}
        onSave={handleSubmit(onSubmit)}
        title="Property Type"
    >
        <>
            <Controller name="name" control={control} render={({ field }) => <TextField margin='dense' error={isError(errors.name)} helperText={errors.name && errors.name.message} fullWidth label='Name' {...field} />} />
            
            <Controller 
                name="hasWaitlist" 
                control={control} 
                render={({ field }) => (
                    <FormControlLabel
                        control={<Checkbox {...field} checked={field.value} />}
                        label="Has Waitlist"
                        sx={{ mt: 1, mb: 1 }}
                    />
                )} 
            />

            <FormControl fullWidth margin='dense' variant='outlined'>
                <InputLabel shrink htmlFor='attrsSelect' sx={{ backgroundColor: 'white', px: 0.5 }}>
                    Attributes (Optional)
                </InputLabel>
                <Select 
                    multiple 
                    native 
                    inputProps={{ id: 'attrsSelect' }} 
                    defaultValue={prpData?.attributes || []}
                    sx={{ mt: 1 }}
                >
                    {attrs.map((e, idx) => {
                        return <option key={idx} value={e._id}>{e.name}</option>
                    })}
                </Select>
            </FormControl>
        </>
    </WorkflowDialogShell>
}
